import React, { useEffect, useMemo, useState } from "react";
import { ISirTable, ISirTableData } from "../../../interfaces/invoice";
import { Button, Col, DatePicker, Form, Input, Row, Table, Select, Flex, Switch, Radio, Modal } from "antd";
import type { ColumnsType } from "antd/es/table";
import { BOOKING_NO_PATTERN, DATE_FORMAT } from "../../../constants/common";
import { renderClassNameStatusInvoice, renderClassNameStatusSir, roundCost } from "../../../utils/helper";
import FormattedNumberInput from "../../../components/format-number-input";
import { SupplierInvoiceRecordStatusEnum } from "../../../constants/enum";
import RejectedModalInvoice from "../../invoice/components/rejected-modal";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { CheckOutlined, CloseOutlined, PlusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useDropdownsInvoiceData } from "../../../hooks/useDropdownsInvoiceData";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { PERMISSIONS } from "../../../constants/permission";
import { useViewFile } from "../../../hooks/useViewFile";
import { WorkflowServices } from "../../../services/workflow";
import { useMessageApi } from "../../../context/MessageContext";
import dayjs from "dayjs";

const defaultRow = {
  status: "",
  bookingNo: undefined,
  amount: undefined,
  leadPax: undefined,
  isActive: true,
  reason: "",
  isViewFe: true,
};

interface MatchingInvoicesFormProps {
  form: any;
  invoices: ISirTableData[];
  creditBalance?: number;
  selectedIndex: number;
  isDisabledByStatus: boolean;
  isTypeFlight: boolean;
  isDirty?: boolean;
}

const MatchingInvoicesForm: React.FC<MatchingInvoicesFormProps> = ({
  form,
  creditBalance = 0,
  selectedIndex = 0,
  invoices = [],
  isDisabledByStatus = true,
  isTypeFlight,
  isDirty = false,
}) => {
  const messageApi = useMessageApi();
  const navigate = useNavigate();
  const hasEditor = useHasPermission([PERMISSIONS.INVOICE_EDITOR]);
  const isDisabledField = !hasEditor || !isDisabledByStatus;
  const { id } = useParams();
  const [isModalRejected, setIsModalRejected] = useState(false);
  const [isGenPdf, setIsGenPdf] = useState(false);
  const [isModalUnsaved, setIsModalUnsaved] = useState(false);
  const [matchingDetailId, setMatchingDetailId] = useState<string>();
  const invoiceDetail = invoices[selectedIndex];
  const { supplierInvoiceRecords = [], supplierInvoiceRecordId: sirId, attachments = [] } = invoiceDetail || {};
  const attachment = attachments?.[0];

  const { data: dropdownsData } = useDropdownsInvoiceData();
  const { currencies = [], suppliers = [] } = dropdownsData || {};
  const { loading: loadingDownload, onViewFile: onDownloadAttachment } = useViewFile();

  useEffect(() => {
    setMatchingDetailId(id);
  }, [id]);

  const filterSelectOption = (input: string, option?: { label: string; name: string }) => {
    const label = option?.label?.toString().toLowerCase() ?? "";
    const name = option?.name?.toLowerCase?.() ?? "";

    return label.includes(input.toLowerCase()) || name.includes(input.toLowerCase());
  };

  const optionRender = (option: any) => <>{`${option.data.label} - ${option.data.name}`}</>;

  const handleChangeSupplierCode = (value: string) => {
    const find = suppliers.find((item) => item.key === value);
    form.setFieldValue(["invoices", selectedIndex, "supplierName"], find?.label);
  };

  const requiredBookingOrLeadPax = (row: ISirTable) => {
    const { bookingNo, leadPax } = row || {};
    if (!isCheckDisabledRow(row) && !bookingNo && !leadPax) {
      return true;
    }
    return false;
  };

  const requiredAmountCreditNote = (row: ISirTable) => {
    const { invoiceMatchId, amount } = row || {};
    if (!isCheckDisabledRow(row) && invoiceMatchId && Number(amount) >= 0) {
      return true;
    }
    return false;
  };

  const isCheckDisabledRow = (row: ISirTable) => {
    const { isActive, status } = row || {};
    if (!isActive || status === SupplierInvoiceRecordStatusEnum.EXPORTED) {
      return true;
    }
    return false;
  };

  const isCredit = (index: number) => {
    return !!supplierInvoiceRecords?.[index]?.invoiceMatchId;
  };

  const setValueFieldBySirRefId = (row: ISirTable, field: string, value: string | boolean) => {
    const { id, invoiceMatchId } = row || {};
    for (let i = 0; i < supplierInvoiceRecords?.length; i++) {
      if (id && !invoiceMatchId && supplierInvoiceRecords?.[i]?.sirRefId === id) {
        form.setFieldValue(
          ["invoices", selectedIndex, "supplierInvoiceRecords", i, field],
          field === "bookingNo" ? `${value ? value + "-CR" : "CR"}` : value
        );
      }
    }
  };

  const setValueFieldByInvoices = (field: any[], value: any) => {
    form.setFieldValue(["invoices", selectedIndex, ...field], value);
  };

  const handleGenPdf = async () => {
    try {
      setIsGenPdf(true);
      const findSir = supplierInvoiceRecords.find((item) => item.id === sirId);
      const dateTime = dayjs(new Date()).format("YYYYMMDD");
      const fileName = `${findSir?.bookingNo}-${
        isTypeFlight ? findSir?.pnrRef : findSir?.leadPax
      }-${dateTime}-generated`;
      const response: Blob = await WorkflowServices.generatePdf(id!, sirId!);

      const blobUrl = window.URL.createObjectURL(response);
      window.open(blobUrl, "_blank");

      // Trigger download
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (error: any) {
      messageApi.error(error?.message || "Generate PDF Failed.");
    } finally {
      setIsGenPdf(false);
    }
  };

  const isShowGenPdf = useMemo(
    () => supplierInvoiceRecords.some((item) => item.id && item?.invoiceMatchId),
    [supplierInvoiceRecords]
  );

  const handleSwitchMatching = (matchingId: string | undefined) => {
    if (!matchingId) return;
    if (isDirty) {
      setIsModalUnsaved(true);
      setMatchingDetailId(matchingId);
    } else {
      navigateSir(matchingId);
    }
  };

  const navigateSir = (matchingId: string | undefined) => {
    if (!matchingId) return;
    navigate(`/${isTypeFlight ? "flight" : "ap"}-workflow/${matchingId}`);
  };

  const renderModalUnsaved = () => (
    <Modal
      open={isModalUnsaved}
      onCancel={() => {
        setMatchingDetailId(id);
        setIsModalUnsaved(false);
      }}
      onOk={() => {
        navigateSir(matchingDetailId);
        setIsModalUnsaved(false);
      }}
      title={"Unsaved Changes"}
      okText="Leave without Saving"
      cancelText="Stay on Page"
      okType="danger"
    >
      You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.
    </Modal>
  );

  return (
    <>
      <Form form={form} layout="horizontal" disabled={isDisabledField}>
        <Form.List name="invoices">
          {(fields) => {
            return (
              <>
                {fields
                  .filter((field) => field.name === selectedIndex)
                  .map(({ key, name, ...restField }) => {
                    return (
                      <div key={key}>
                        {invoiceDetail && (
                          <>
                            <Row gutter={24}>
                              <Col span={12}>
                                <Row align={"middle"} style={{ marginBottom: 24 }}>
                                  <Col span={10} style={{ textAlign: "right", paddingRight: 10 }}>
                                    Invoice {isTypeFlight ? "Name" : ""}
                                  </Col>
                                  <Col span={14}>
                                    {isTypeFlight ? (
                                      <>Flight Invoice</>
                                    ) : (
                                      <Button
                                        disabled={loadingDownload}
                                        type="link"
                                        onClick={() =>
                                          onDownloadAttachment?.(
                                            Number(invoiceDetail.id),
                                            attachment?.id!,
                                            attachment?.fileName!
                                          )
                                        }
                                        style={{ padding: 0, height: 32 }}
                                      >
                                        View Attached Invoice <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                      </Button>
                                    )}
                                  </Col>
                                </Row>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  {...restField}
                                  label="Supplier Code"
                                  name={[name, "supplierId"]}
                                  labelCol={{ span: 10 }}
                                  rules={[{ required: true, message: "Complete this required field." }]}
                                >
                                  <Select
                                    showSearch
                                    allowClear
                                    filterOption={filterSelectOption}
                                    options={suppliers.map((item) => ({
                                      value: item.key,
                                      label: item.code,
                                      name: item.label,
                                    }))}
                                    placeholder="Select"
                                    optionRender={optionRender}
                                    onChange={handleChangeSupplierCode}
                                    disabled
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  {...restField}
                                  label="Supplier Name"
                                  name={[name, "supplierName"]}
                                  labelCol={{ span: 10 }}
                                  rules={[{ required: true, message: "Complete this required field." }]}
                                >
                                  <Input disabled />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  {...restField}
                                  label="Invoice Number"
                                  name={[name, "invoiceNumber"]}
                                  labelCol={{ span: 10 }}
                                  rules={[{ required: isTypeFlight, message: "Complete this required field." }]}
                                >
                                  <Input />
                                </Form.Item>
                              </Col>
                              {isTypeFlight ? (
                                <Col span={12}>
                                  <Form.Item
                                    {...restField}
                                    label="Transaction Date"
                                    name={[name, "transactionDate"]}
                                    labelCol={{ span: 10 }}
                                    rules={[{ required: true, message: "Complete this required field." }]}
                                  >
                                    <DatePicker
                                      className="w-full"
                                      format={DATE_FORMAT}
                                      placeholder="Select date"
                                      disabled
                                    />
                                  </Form.Item>
                                </Col>
                              ) : (
                                <>
                                  <Col span={12}>
                                    <Form.Item
                                      {...restField}
                                      label="Receive Date"
                                      name={[name, "invoiceEmail", "receivedAt"]}
                                      labelCol={{ span: 10 }}
                                    >
                                      <DatePicker
                                        className="w-full"
                                        format={DATE_FORMAT}
                                        placeholder="Select date"
                                        disabled
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col span={12}>
                                    <Form.Item
                                      {...restField}
                                      label="Invoice Date"
                                      name={[name, "invoiceDate"]}
                                      labelCol={{ span: 10 }}
                                      rules={[{ required: true, message: "Complete this required field." }]}
                                    >
                                      <DatePicker className="w-full" format={DATE_FORMAT} placeholder="Select date" />
                                    </Form.Item>
                                  </Col>
                                </>
                              )}

                              <Col span={12}>
                                <Form.Item
                                  {...restField}
                                  label="Currency"
                                  name={[name, "currency"]}
                                  labelCol={{ span: 10 }}
                                  rules={[{ required: true, message: "Complete this required field." }]}
                                >
                                  <Select
                                    showSearch
                                    allowClear
                                    filterOption={filterSelectOption}
                                    options={currencies.map((item) => ({
                                      value: item.code,
                                      label: item.code,
                                      name: item.label,
                                    }))}
                                    placeholder="Select"
                                    optionRender={optionRender}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  {...restField}
                                  label="Total Invoice Amount"
                                  name={[name, "totalAmount"]}
                                  labelCol={{ span: 10 }}
                                  rules={[{ required: true, message: "Complete this required field." }]}
                                >
                                  <FormattedNumberInput className="w-full" />
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row align={"middle"}>
                              <Col span={5} style={{ textAlign: "right", paddingRight: 15 }}>
                                Invoice Status
                              </Col>
                              <Col span={19}>
                                <Flex align="center" justify="space-between">
                                  <span className={renderClassNameStatusInvoice(invoiceDetail.status)}>
                                    {invoiceDetail.status}
                                  </span>
                                  <Button type="link" onClick={() => setIsModalRejected(true)}>
                                    Reject Invoice
                                  </Button>
                                </Flex>
                              </Col>
                            </Row>
                            {!isDisabledField && (
                              <Form.Item shouldUpdate noStyle>
                                {({ getFieldValue }) => {
                                  const totalAmount = getFieldValue(["invoices", name, "totalAmount"]) || 0;
                                  const sirTables = getFieldValue(["invoices", name, "supplierInvoiceRecords"]) || [];
                                  const sirTotalAmount = sirTables
                                    .filter((r: ISirTable) => r.isActive)
                                    .reduce((sum: number, item: any) => sum + (item.amount || 0), 0);

                                  const isValidBookingOrLeadPax = sirTables.some((sir: any) =>
                                    requiredBookingOrLeadPax(sir)
                                  );

                                  const isValidAmountCreditNote = sirTables.some((sir: any) =>
                                    requiredAmountCreditNote(sir)
                                  );

                                  return (
                                    <div className="text-error" style={{ textAlign: "center", marginBottom: 16 }}>
                                      {roundCost(totalAmount) !== roundCost(sirTotalAmount) && (
                                        <div>{`Cost mismatched between SIR and Invoice, the remaining cost is ${(
                                          totalAmount - sirTotalAmount
                                        ).toFixed(2)}`}</div>
                                      )}
                                      {isValidBookingOrLeadPax && (
                                        <div>
                                          Please fill out the <strong>Booking Reference ID</strong> or{" "}
                                          <strong>Lead Passenger</strong> fields before saving the invoice record
                                        </div>
                                      )}
                                      {isValidAmountCreditNote && <div>Credit note must be negative amount</div>}
                                    </div>
                                  );
                                }}
                              </Form.Item>
                            )}
                            <Form.List name={[name, "supplierInvoiceRecords"]}>
                              {(sirFields, { add, remove }) => {
                                const columns: ColumnsType<any> = [
                                  {
                                    title: "",
                                    dataIndex: "selectCheckbox",
                                    width: 40,
                                    align: "center" as const,
                                    render: (_: any, record: any) => {
                                      const matchingId = supplierInvoiceRecords?.[record.name]?.matchingId;
                                      return (
                                        <Radio
                                          checked={matchingId === matchingDetailId && !isCredit(record.name)}
                                          onChange={() => handleSwitchMatching(matchingId)}
                                          disabled={!matchingId || isCredit(record.name)}
                                          key={`${matchingDetailId}`}
                                        />
                                      );
                                    },
                                  },
                                  {
                                    title: "Status",
                                    dataIndex: "status",
                                    width: 110,
                                    render: (_: any, record: any) => {
                                      const status = supplierInvoiceRecords?.[record.name]?.status;
                                      return <span className={renderClassNameStatusSir(status || "")}>{status}</span>;
                                    },
                                  },
                                  {
                                    title: "Booking Reference ID",
                                    dataIndex: "bookingNo",
                                    width: 140,
                                    render: (_: any, record: any) => (
                                      <Form.Item
                                        name={[record.name, "bookingNo"]}
                                        rules={[
                                          {
                                            required: isTypeFlight
                                              ? true
                                              : requiredBookingOrLeadPax(supplierInvoiceRecords?.[record.name]),
                                            message: "",
                                          },
                                          ...(isCredit(record.name)
                                            ? []
                                            : [
                                                {
                                                  pattern: BOOKING_NO_PATTERN,
                                                  message: "",
                                                  validateTrigger: "onBlur",
                                                },
                                              ]),
                                          {
                                            validator: (_, value) => {
                                              if (value && !value.trim()) {
                                                return Promise.reject(new Error(""));
                                              }
                                              return Promise.resolve();
                                            },
                                          },
                                        ]}
                                      >
                                        <Input
                                          placeholder="Enter booking ref"
                                          disabled={
                                            isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) ||
                                            isDisabledField ||
                                            isCredit(record.name)
                                          }
                                          onChange={(e) => {
                                            form.validateFields([
                                              [
                                                "invoices",
                                                selectedIndex,
                                                "supplierInvoiceRecords",
                                                record.name,
                                                "leadPax",
                                              ],
                                            ]);
                                            setValueFieldBySirRefId(
                                              supplierInvoiceRecords?.[record.name],
                                              "bookingNo",
                                              e.target.value
                                            );
                                          }}
                                        />
                                      </Form.Item>
                                    ),
                                  },
                                  ...(isTypeFlight
                                    ? [
                                        {
                                          title: "PNR Ref",
                                          dataIndex: "pnrRef",
                                          width: 130,
                                          render: (_: any, record: any) => (
                                            <Form.Item
                                              name={[record.name, "pnrRef"]}
                                              rules={[
                                                {
                                                  required: true,
                                                  message: "",
                                                },
                                              ]}
                                            >
                                              <Input
                                                placeholder="Enter PNR Ref"
                                                disabled={
                                                  isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) ||
                                                  isDisabledField ||
                                                  isCredit(record.name)
                                                }
                                                onChange={(e) => {
                                                  setValueFieldBySirRefId(
                                                    supplierInvoiceRecords?.[record.name],
                                                    "pnrRef",
                                                    e.target.value
                                                  );
                                                }}
                                              />
                                            </Form.Item>
                                          ),
                                        },
                                      ]
                                    : [
                                        {
                                          title: "Lead Pax",
                                          dataIndex: "leadPax",
                                          width: 130,
                                          render: (_: any, record: any) => (
                                            <Form.Item
                                              name={[record.name, "leadPax"]}
                                              rules={[
                                                {
                                                  required: requiredBookingOrLeadPax(
                                                    supplierInvoiceRecords?.[record.name]
                                                  ),
                                                  message: "",
                                                },
                                              ]}
                                            >
                                              <Input
                                                placeholder="Enter Lead Passenger"
                                                disabled={
                                                  isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) ||
                                                  isDisabledField ||
                                                  isCredit(record.name)
                                                }
                                                onChange={(e) => {
                                                  form.validateFields([
                                                    [
                                                      "invoices",
                                                      selectedIndex,
                                                      "supplierInvoiceRecords",
                                                      record.name,
                                                      "bookingNo",
                                                    ],
                                                  ]);
                                                  setValueFieldBySirRefId(
                                                    supplierInvoiceRecords?.[record.name],
                                                    "leadPax",
                                                    e.target.value
                                                  );
                                                }}
                                              />
                                            </Form.Item>
                                          ),
                                        },
                                      ]),

                                  {
                                    title: "Amount",
                                    dataIndex: "amount",
                                    width: 140,
                                    render: (_: any, record: any) => (
                                      <Form.Item
                                        name={[record.name, "amount"]}
                                        rules={[
                                          { required: supplierInvoiceRecords?.[record.name]?.isActive, message: "" },
                                        ]}
                                      >
                                        <FormattedNumberInput
                                          className="w-full input-t-r"
                                          placeholder="Enter amount"
                                          disabled={
                                            isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) || isDisabledField
                                          }
                                          {...(isCredit(record.name) &&
                                            Number(supplierInvoiceRecords?.[record.name]?.amount) >= 0 && {
                                              status: "error",
                                            })}
                                        />
                                      </Form.Item>
                                    ),
                                  },
                                  {
                                    title: "Active",
                                    dataIndex: "isActive",
                                    width: 70,
                                    align: "center" as const,
                                    render: (_: any, record: any) => {
                                      if (supplierInvoiceRecords?.[record.name]?.id && !isCredit(record.name)) {
                                        return (
                                          <Form.Item
                                            name={[record.name, "isActive"]}
                                            valuePropName="checked"
                                            style={{ display: "inline-block" }}
                                          >
                                            <Switch
                                              checkedChildren={<CheckOutlined />}
                                              unCheckedChildren={<CloseOutlined />}
                                              onChange={(value) => {
                                                if (value) {
                                                  setValueFieldByInvoices(
                                                    ["supplierInvoiceRecords", record.name, "reason"],
                                                    ""
                                                  );
                                                }
                                                setValueFieldBySirRefId(
                                                  supplierInvoiceRecords?.[record.name],
                                                  "isActive",
                                                  value
                                                );

                                                form.validateFields([
                                                  [
                                                    "invoices",
                                                    selectedIndex,
                                                    "supplierInvoiceRecords",
                                                    record.name,
                                                    "bookingNo",
                                                  ],
                                                  [
                                                    "invoices",
                                                    selectedIndex,
                                                    "supplierInvoiceRecords",
                                                    record.name,
                                                    isTypeFlight ? "pnrRef" : "leadPax",
                                                  ],
                                                  [
                                                    "invoices",
                                                    selectedIndex,
                                                    "supplierInvoiceRecords",
                                                    record.name,
                                                    "reason",
                                                  ],
                                                ]);
                                                if (isTypeFlight) {
                                                  setValueFieldByInvoices(
                                                    ["totalAmount"],
                                                    value ? supplierInvoiceRecords?.[record.name]?.amount : 0
                                                  );
                                                }
                                              }}
                                            />
                                          </Form.Item>
                                        );
                                      }
                                      return null;
                                    },
                                  },
                                  {
                                    title: "Credit Notes / Reason",
                                    dataIndex: "note",
                                    width: 180,
                                    render: (_: any, record: any) => {
                                      if (isCredit(record.name)) {
                                        return (
                                          <Form.Item name={[record.name, "note"]}>
                                            <Input
                                              placeholder="Enter note here"
                                              disabled={
                                                !isCredit(record.name) ||
                                                isDisabledField ||
                                                isCheckDisabledRow(supplierInvoiceRecords?.[record.name])
                                              }
                                            />
                                          </Form.Item>
                                        );
                                      }
                                      return (
                                        <Form.Item
                                          name={[record.name, "reason"]}
                                          rules={[
                                            { required: !supplierInvoiceRecords?.[record.name]?.isActive, message: "" },
                                          ]}
                                        >
                                          <Input
                                            placeholder={
                                              supplierInvoiceRecords?.[record.name]?.isActive ? "" : "Enter Reason"
                                            }
                                            disabled={supplierInvoiceRecords?.[record.name]?.isActive}
                                          />
                                        </Form.Item>
                                      );
                                    },
                                  },
                                  {
                                    title: "",
                                    dataIndex: "action",
                                    width: 50,
                                    render: (_: any, record: any) => {
                                      if (isCredit(record.name) || !supplierInvoiceRecords?.[record.name]?.id) {
                                        return (
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              remove(record.name);
                                            }}
                                            type="link"
                                            icon={<CloseOutlined />}
                                          />
                                        );
                                      }
                                      return null;
                                    },
                                  },
                                ];

                                const activeFields = sirFields.filter((f) => {
                                  const item = form.getFieldValue(["invoices", name, "supplierInvoiceRecords", f.name]);
                                  return item?.isViewFe || (!!sirId && item?.id === sirId);
                                });

                                return (
                                  <>
                                    <Flex align="center" justify="space-between">
                                      <h4 className="sub-title">Supplier Invoice Records</h4>
                                      <Button
                                        type="link"
                                        onClick={() => {
                                          const sir = supplierInvoiceRecords.find((item: any) => item.id === sirId);
                                          const indexOfSir = supplierInvoiceRecords.findIndex(
                                            (item: any) => item.id === sirId
                                          );
                                          const newIndex =
                                            indexOfSir >= 0 ? indexOfSir + 1 : supplierInvoiceRecords.length;

                                          add(
                                            {
                                              ...sir,
                                              status: "Unmatched",
                                              invoiceMatchId: id,
                                              creditNote: "",
                                              id: "",
                                              sirRefId: sir?.id,
                                              amount: Number(creditBalance.toFixed(2)),
                                              isViewFe: true,
                                              bookingNo: `${sir?.bookingNo ? sir?.bookingNo + "-CR" : "CR"}`,
                                            },
                                            newIndex
                                          );
                                          setTimeout(() => {
                                            const rowEl = document.getElementById(`sir-row-${newIndex}`);
                                            if (rowEl) {
                                              rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
                                            }
                                          }, 100);
                                        }}
                                        icon={<PlusCircleOutlined />}
                                        disabled={(activeFields && activeFields.length === 0) || isDisabledField}
                                      >
                                        Add Credit Note
                                      </Button>
                                    </Flex>

                                    <Table
                                      dataSource={activeFields.length === 0 ? fields || [] : activeFields}
                                      pagination={false}
                                      rowKey={(f) => f.key}
                                      columns={columns}
                                      size="small"
                                      className="table-fm"
                                      scroll={{ y: 50 * 5 }}
                                      rowClassName={(record) => {
                                        const item = form.getFieldValue([
                                          "invoices",
                                          name,
                                          "supplierInvoiceRecords",
                                          record.name,
                                        ]);
                                        const classes = [];
                                        if (item?.id === sirId) classes.push("active-row");
                                        if (isCredit(record.name)) classes.push("active-credit");

                                        return classes.join(" ");
                                      }}
                                      onRow={(record) => {
                                        return { id: `sir-row-${record.name}` } as any;
                                      }}
                                    />
                                    {!isTypeFlight && !isDisabledField && (
                                      <Button
                                        color="primary"
                                        variant="outlined"
                                        block
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                          add({ ...defaultRow });
                                          setTimeout(() => {
                                            const tableBody = document.querySelector(
                                              ".table-fm .ant-table-body"
                                            ) as HTMLElement;

                                            if (tableBody) {
                                              tableBody.scrollTo({
                                                top: tableBody.scrollHeight,
                                                behavior: "smooth",
                                              });
                                            }
                                          }, 100);
                                        }}
                                        style={{ marginTop: "16px" }}
                                      >
                                        Add row
                                      </Button>
                                    )}
                                  </>
                                );
                              }}
                            </Form.List>
                          </>
                        )}
                      </div>
                    );
                  })}
              </>
            );
          }}
        </Form.List>
      </Form>
      {isShowGenPdf && (
        <Button loading={isGenPdf} type="link" onClick={handleGenPdf} style={{ padding: 0, height: 32, marginTop: 10 }}>
          Generate PDF
        </Button>
      )}

      {invoiceDetail && (
        <RejectedModalInvoice
          isVisible={isModalRejected}
          onClose={(isTrigger) => {
            setIsModalRejected(false);
            if (isTrigger) {
              navigate(isTypeFlight ? "/flight-workflow" : "/ap-workflow");
            }
          }}
          invoiceDetail={invoiceDetail}
          onGoToEdit={(invoice) => {}}
        />
      )}

      {renderModalUnsaved()}
    </>
  );
};

export default MatchingInvoicesForm;
